// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Taking a signature apart, so the page can set part of it back.
//
// The signature arrives as one string. It is the longest line in an entry and
// the one people actually read, so it is split here rather than printed whole:
// the page pushes defaults and the return arrow back a step, and wraps between
// parameters instead of growing a horizontal scrollbar that would hide the
// return type off the right edge.
// ==========================================================================

/** One parameter as it appears in the signature. */
export interface SignaturePart {
  /** `name: str` - the part that stays at full strength. */
  text: string;
  /** `0`, `''` - what follows the `=`, receded on the page. Empty if none. */
  fallback: string;
}

export interface ParsedSignature {
  parts: SignaturePart[];
  /** `-> str`, or empty when the function is not annotated. */
  tail: string;
}

const OPENERS = '([{';
const CLOSERS = ')]}';

/**
 * Split on commas that are not inside brackets or quotes.
 *
 * `Union[str, list, tuple]` is one type containing two commas, so a plain
 * `split(',')` tears it into three parameters. Quote tracking is deliberately
 * simple - a default value carrying an escaped quote is not a thing that
 * appears in these packages, and guessing wrong only affects wrapping.
 */
const splitTopLevel = (inner: string): string[] => {
  const found: string[] = [];
  let depth = 0;
  let quote = '';
  let start = 0;

  for (let index = 0; index < inner.length; index += 1) {
    const character = inner[index];

    if (quote) {
      if (character === quote) {
        quote = '';
      }
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
    } else if (OPENERS.includes(character)) {
      depth += 1;
    } else if (CLOSERS.includes(character)) {
      depth -= 1;
    } else if (character === ',' && depth === 0) {
      found.push(inner.slice(start, index));
      start = index + 1;
    }
  }

  const last = inner.slice(start);
  if (last.trim()) {
    found.push(last);
  }
  return found;
};

/** The index of the `=` that separates a default, or -1. */
const defaultAt = (part: string): number => {
  let depth = 0;
  let quote = '';

  for (let index = 0; index < part.length; index += 1) {
    const character = part[index];

    if (quote) {
      if (character === quote) {
        quote = '';
      }
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
    } else if (OPENERS.includes(character)) {
      depth += 1;
    } else if (CLOSERS.includes(character)) {
      depth -= 1;
    } else if (character === '=' && depth === 0) {
      // Not `==`, and not the `>=` of an annotation, though neither should
      // reach here; cheap to be sure.
      if (part[index + 1] !== '=' && part[index - 1] !== '=') {
        return index;
      }
    }
  }
  return -1;
};

export const parseSignature = (signature: string): ParsedSignature => {
  let depth = 0;
  let quote = '';
  let close = -1;

  for (let index = 0; index < signature.length; index += 1) {
    const character = signature[index];

    if (quote) {
      if (character === quote) {
        quote = '';
      }
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
    } else if (OPENERS.includes(character)) {
      depth += 1;
    } else if (CLOSERS.includes(character)) {
      depth -= 1;
      if (depth === 0) {
        close = index;
        break;
      }
    }
  }

  if (close < 0) {
    return { parts: [], tail: '' };
  }

  const parts = splitTopLevel(signature.slice(1, close)).map((raw) => {
    const trimmed = raw.trim();
    const at = defaultAt(trimmed);
    return at < 0
      ? { text: trimmed, fallback: '' }
      : {
          text: trimmed.slice(0, at).trim(),
          fallback: trimmed.slice(at + 1).trim()
        };
  });

  return { parts, tail: signature.slice(close + 1).trim() };
};

/**
 * The alias the framework's own code would import this module under.
 *
 * `import core.maya.psd as sb_psd` is the convention throughout core, mix and
 * sbgui, so a snippet copied from here drops into a scene file looking like
 * the code beside it rather than like documentation.
 */
export const moduleAlias = (dotted: string): string =>
  `sb_${dotted.split('.').pop() ?? dotted}`;

/**
 * An import line and a call, ready to paste into the script editor.
 *
 * Optional parameters are left out. What somebody wants off a reference page
 * is the shortest call that runs; the full set of switches is on the page
 * directly above, and pasting twelve defaulted arguments only makes work.
 */
export const buildSnippet = (
  dotted: string,
  name: string,
  signature: string,
  className?: string
): string => {
  const alias = moduleAlias(dotted);
  const required = parseSignature(signature)
    .parts.filter((part) => !part.fallback)
    .map((part) => part.text.split(':')[0].trim())
    .filter(
      (argument) =>
        argument &&
        argument !== 'self' &&
        argument !== 'cls' &&
        argument !== '*' &&
        argument !== '/' &&
        !argument.startsWith('*')
    );

  const call = className ? `${alias}.${className}.${name}` : `${alias}.${name}`;
  return `import ${dotted} as ${alias}\n\n${call}(${required.join(', ')})`;
};
